import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ArrowDownRight, ArrowUpRight, CircleAlert, Crosshair, Flame, Inbox, Layers, TrendingDown, TrendingUp, Wallet } from 'lucide-react'

import { PageHeader } from '../components/layout'
import { getOperatorPositions, liquidatePosition, type AdminPosition } from '../api/admin'
import {
  Avatar,
  Badge,
  Button,
  Callout,
  EmptyState,
  Field,
  Input,
  Modal,
  Pager,
  Panel,
  StatCard,
  StatusPill,
  Table,
  TableSkeleton,
  Tabs,
  Textarea,
  cx,
  describeError,
  formatNumber,
  money,
  short,
  tableClass,
  useToast,
  useTotp,
  when,
} from '../components/ui/Ui'
import styles from './Futures.module.css'

type Tab = 'open' | 'liquidated' | 'closed' | 'all'

const TABS: ReadonlyArray<{ value: Tab; label: string }> = [
  { value: 'open', label: 'Открытые' },
  { value: 'liquidated', label: 'Ликвидированные' },
  { value: 'closed', label: 'Закрытые' },
  { value: 'all', label: 'Все' },
]

const SIDE_LABEL: Record<AdminPosition['side'], string> = { long: 'лонг', short: 'шорт' }
const SIDE_TITLE: Record<AdminPosition['side'], string> = { long: 'Лонг', short: 'Шорт' }
const ACCOUNT_LABEL: Record<AdminPosition['accountType'], string> = { spot: 'спот', futures: 'свой счёт', copy: 'копирование' }
const MODE_LABEL: Record<AdminPosition['marginMode'], string> = { isolated: 'изолированная', cross: 'кросс' }

function plural(count: number, one: string, few: string, many: string): string {
  const tail = count % 100
  if (tail >= 11 && tail <= 14) return many
  if (count % 10 === 1) return one
  if (count % 10 >= 2 && count % 10 <= 4) return few
  return many
}

/** A price: two decimals, whatever precision the engine keeps. */
function price(value: string | null): string {
  return value === null ? '—' : money(value, 2, 2)
}

/** A result with its sign (a real minus) and colour: the row is read at a glance. */
function Signed({ value }: { value: string | null }): JSX.Element {
  if (value === null) return <span className={tableClass.muted}>—</span>
  const n = Number(value)
  const text = money(String(Math.abs(n)), 2, 2)
  // Below a cent the sign would claim a result the two decimals do not show.
  if (Math.abs(n) < 0.005) return <span>{money('0', 2, 2)}</span>
  if (n > 0) return <span className={styles.up}>+{text}</span>
  if (n < 0) return <span className={styles.down}>−{text}</span>
  return <span>{text}</span>
}

/** How far the mark may still move against the position before the engine liquidates it, as a share of the mark. */
function distanceToLiquidation(row: AdminPosition): number | null {
  if (row.status !== 'open' || row.markPrice === null || row.liquidationPrice === null) return null
  const mark = Number(row.markPrice)
  const liq = Number(row.liquidationPrice)
  if (!Number.isFinite(mark) || !Number.isFinite(liq) || mark <= 0) return null
  return row.side === 'long' ? (mark - liq) / mark : (liq - mark) / mark
}

/** «Лонг» / «Шорт» as a pill in the direction's tone. */
function Side({ side, size = 'sm' }: { side: AdminPosition['side']; size?: 'sm' | 'md' }): JSX.Element {
  return (
    <Badge size={size} tone={side === 'long' ? 'accent' : 'down'} icon={side === 'long' ? <ArrowUpRight /> : <ArrowDownRight />}>
      {SIDE_TITLE[side]}
    </Badge>
  )
}

/** The leverage as a small mono chip: «5×». */
function Leverage({ value }: { value: number }): JSX.Element {
  return <span className={styles.leverage}>{value}×</span>
}

/**
 * The open book at a glance, from the same query as the «Открытые» tab
 * (one cache entry): how many positions, how much margin is in them, their
 * running result and the one closest to its liquidation price.
 */
function OpenBook(): JSX.Element {
  const open = useQuery({ queryKey: ['operator-positions', 'open', 1], queryFn: () => getOperatorPositions('open', 1), refetchInterval: 15_000 })
  if (open.isError) return <Callout tone="down">{describeError(open.error)}</Callout>

  const items = open.data?.items ?? []
  const loading = open.isPending
  const longs = items.filter((row) => row.side === 'long').length
  const isolated = items.filter((row) => row.marginMode === 'isolated').length
  const margin = items.reduce((sum, row) => sum + Number(row.margin), 0)
  const pnl = items.reduce((sum, row) => sum + (row.unrealizedPnl === null ? 0 : Number(row.unrealizedPnl)), 0)
  const hasPnl = Math.abs(pnl) >= 0.005
  const unit = items.every((row) => row.marketSymbol.includes('/USDT')) ? 'USDT' : undefined
  const closest = items.reduce<{ row: AdminPosition; distance: number } | null>((best, row) => {
    const distance = distanceToLiquidation(row)
    if (distance === null) return best
    return best === null || distance < best.distance ? { row, distance } : best
  }, null)
  // More open positions than one page: the sums speak of the first page only, and say so.
  const scope = open.data !== undefined && open.data.total > items.length ? `по первым ${items.length}` : undefined

  return (
    <div className={cx('kpi-grid', styles.book)}>
      <StatCard
        label="Открытые позиции"
        value={open.data?.total ?? null}
        icon={<Layers />}
        tone="info"
        loading={loading}
        hint={items.length > 0 ? `лонг ${longs} · шорт ${items.length - longs}` : 'позиций нет'}
      />
      <StatCard
        label="Маржа в позициях"
        value={margin.toFixed(2)}
        unit={unit}
        icon={<Wallet />}
        tone="neutral"
        loading={loading}
        hint={scope ?? (items.length > 0 ? `изолированная ${isolated} · кросс ${items.length - isolated}` : undefined)}
      />
      <StatCard
        label="Нереализованный PnL"
        value={(hasPnl ? pnl : 0).toFixed(2)}
        format={(n) => `${n >= 0.005 ? '+' : ''}${formatNumber(n, 2)}`}
        unit={unit}
        icon={pnl < 0 && hasPnl ? <TrendingDown /> : <TrendingUp />}
        tone={!hasPnl ? 'neutral' : pnl < 0 ? 'down' : 'accent'}
        loading={loading}
        delta={margin > 0 && hasPnl ? (pnl / margin) * 100 : null}
        deltaLabel={margin > 0 && hasPnl ? 'к марже' : undefined}
        hint={scope}
      />
      <StatCard
        label="Ближе всех к ликвидации"
        value={closest ? (closest.distance * 100).toFixed(1) : null}
        unit={closest ? '%' : undefined}
        icon={<Crosshair />}
        tone={closest && closest.distance < 0.1 ? 'down' : 'warning'}
        loading={loading}
        hint={
          closest
            ? `${closest.row.marketSymbol} · ${SIDE_LABEL[closest.row.side]} ${closest.row.leverage}× · ${short(closest.row.userEmail, 16, 0)}`
            : 'нет открытых позиций с ценой ликвидации'
        }
      />
    </div>
  )
}

/**
 * Every leveraged position on the platform and the one thing an operator may
 * do to it: liquidate it by hand. The settlement is the engine's own — margin
 * up to the loss, the surplus to the insurance fund, the user notified — but
 * the decision carries a reason, the second factor and an audit row. The
 * price comes from the feed; when the feed is stale the request is refused
 * and the operator may name the price explicitly, which is then on record.
 */
export default function Futures(): JSX.Element {
  const [tab, setTab] = useState<Tab>('open')
  const [page, setPage] = useState(1)
  const [target, setTarget] = useState<AdminPosition | null>(null)
  const [reason, setReason] = useState('')
  const [priceInput, setPriceInput] = useState('')
  const queryClient = useQueryClient()
  const toast = useToast()
  const withTotp = useTotp()

  const status = tab === 'all' ? undefined : tab
  const list = useQuery({ queryKey: ['operator-positions', tab, page], queryFn: () => getOperatorPositions(status, page), refetchInterval: 15_000 })

  // Each position starts from a clean form: a reason or an operator price typed
  // for another position must never carry over to this one.
  const openFor = (row: AdminPosition): void => {
    setReason('')
    setPriceInput('')
    setTarget(row)
  }

  const liquidate = useMutation({
    mutationFn: ({ row, code }: { row: AdminPosition; code: string }) =>
      liquidatePosition(row.id, priceInput.trim() === '' ? { reason: reason.trim() } : { reason: reason.trim(), price: priceInput.trim() }, code),
    onSuccess: (result) => {
      toast(`Позиция ${short(result.position.id, 8, 0)} ликвидирована по ${price(result.markPrice)} (${result.priceSource === 'feed' ? 'цена фида' : 'цена оператора'})`)
      setTarget(null)
      setReason('')
      setPriceInput('')
      void queryClient.invalidateQueries({ queryKey: ['operator-positions'] })
      void queryClient.invalidateQueries({ queryKey: ['overview'] })
    },
    onError: (error) => {
      toast(describeError(error), true)
      void queryClient.invalidateQueries({ queryKey: ['operator-positions'] })
    },
  })

  const rows = list.data?.items ?? []
  const priceValid = priceInput.trim() === '' || (/^\d+(\.\d+)?$/.test(priceInput.trim()) && Number(priceInput) > 0)
  const inProfit = target !== null && target.unrealizedPnl !== null && Number(target.unrealizedPnl) > 0
  const headers = ['Позиция', 'Пользователь · открыта', 'Объём', tab === 'open' ? 'Вход · марк' : 'Вход · цена', 'Ликвидация', 'Маржа · PnL', '']

  return (
    <>
      <PageHeader title="Фьючерсы" description="Позиции с плечом и ручная ликвидация — с причиной и кодом 2FA." />

      <div className="page-stack">
        <OpenBook />

        <Panel className={styles.desk}>
          <div className={styles.head}>
            <Tabs
              value={tab}
              options={TABS}
              onChange={(next) => {
                setTab(next)
                setPage(1)
              }}
              variant="underline"
              ariaLabel="Статус позиций"
              className={styles.tabs}
            />
            {list.data ? (
              <span className={styles.total}>
                <span className="num">{list.data.total}</span> {plural(list.data.total, 'позиция', 'позиции', 'позиций')}
              </span>
            ) : null}
          </div>

          {list.isError ? (
            <EmptyState compact tone="error" icon={<CircleAlert />} title="Список не загрузился" description={describeError(list.error)} />
          ) : list.isPending ? (
            <TableSkeleton rows={5} headers={headers} />
          ) : rows.length === 0 ? (
            <EmptyState
              compact
              icon={<Inbox />}
              title={tab === 'open' ? 'Открытых плечевых позиций нет.' : 'Позиций нет.'}
              description={tab === 'open' ? 'Как только пользователь откроет позицию с плечом, она появится здесь.' : undefined}
            />
          ) : (
            <Table ariaLabel="Позиции">
              <thead>
                <tr>
                  <th>{headers[0]}</th>
                  <th>{headers[1]}</th>
                  <th className={tableClass.right}>{headers[2]}</th>
                  <th className={tableClass.right}>{headers[3]}</th>
                  <th className={tableClass.right}>{headers[4]}</th>
                  <th className={tableClass.right}>{headers[5]}</th>
                  <th>
                    <span className="sr-only">Действия</span>
                  </th>
                </tr>
              </thead>
              <tbody className={styles.rows}>
                {rows.map((row) => {
                  const result = row.status === 'open' ? row.unrealizedPnl : row.realizedPnl
                  const reference = row.status === 'open' ? row.markPrice : row.closePrice
                  const distance = distanceToLiquidation(row)
                  return (
                    <tr key={row.id}>
                      <td>
                        <span className={styles.market}>{row.marketSymbol}</span>
                        <div className={styles.position}>
                          <Side side={row.side} />
                          <Leverage value={row.leverage} />
                          <span className={styles.mode}>{MODE_LABEL[row.marginMode]}</span>
                        </div>
                      </td>
                      <td>
                        <div className={styles.person}>
                          <Avatar name={row.userEmail} size={28} className={styles.rowAvatar} />
                          <div className={styles.personText}>
                            <div className={styles.email} title={row.userEmail}>
                              {row.userEmail}
                            </div>
                            <div className={tableClass.sub}>
                              {ACCOUNT_LABEL[row.accountType]} · {when(row.openedAt)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className={`${tableClass.right} num`}>{money(row.quantity, 6)}</td>
                      <td className={`${tableClass.right} num`}>
                        {price(row.entryPrice)}
                        <div className={styles.subNum} title={row.status === 'open' ? 'Марк: цена фида сейчас' : row.status === 'liquidated' ? 'Цена ликвидации' : 'Цена закрытия'}>
                          {price(reference)}
                        </div>
                      </td>
                      <td className={`${tableClass.right} num`}>
                        <span className={row.status === 'open' && row.liquidationPrice !== null ? styles.liq : undefined}>{price(row.liquidationPrice)}</span>
                        {distance !== null ? (
                          <div className={cx(styles.subNum, distance < 0.1 ? styles.near : '')} title="Сколько марк может пройти против позиции до ликвидации движком">
                            {formatNumber(distance * 100, 1)} % до ликв.
                          </div>
                        ) : null}
                      </td>
                      <td className={`${tableClass.right} num`}>
                        {money(row.margin, 2, 2)}
                        <div className={styles.subNum}>
                          <Signed value={result} />
                        </div>
                      </td>
                      <td className={tableClass.actions}>
                        {row.status === 'open' ? (
                          <Button size="sm" variant="ghost" className={styles.dangerGhost} onClick={() => openFor(row)}>
                            Ликвидировать
                          </Button>
                        ) : (
                          <span title={row.closedAt ? `Закрыта ${when(row.closedAt)}` : undefined}>
                            <StatusPill status={row.status} />
                          </span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          )}
          {list.data ? <Pager page={page} pageSize={list.data.pageSize} total={list.data.total} onPage={setPage} /> : null}
        </Panel>
      </div>

      <Modal
        open={target !== null}
        onClose={() => setTarget(null)}
        title="Ликвидировать позицию"
        icon={<Flame />}
        tone="down"
        subtitle={target === null ? undefined : `${target.userEmail} · ${ACCOUNT_LABEL[target.accountType]}`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setTarget(null)}>
              Отмена
            </Button>
            <Button
              variant="danger-solid"
              loading={liquidate.isPending}
              disabled={reason.trim().length < 3 || !priceValid}
              onClick={() => {
                if (target === null) return
                void withTotp((code) => liquidate.mutateAsync({ row: target, code }))
              }}
            >
              {priceInput.trim() === '' ? 'Ликвидировать по цене фида' : `Ликвидировать по ${price(priceInput.trim())}`}
            </Button>
          </>
        }
      >
        {target !== null ? (
          <div className={styles.summary}>
            <div className={styles.summaryHead}>
              <span className={styles.summaryMarket}>{target.marketSymbol}</span>
              <Side side={target.side} size="md" />
              <Leverage value={target.leverage} />
              <span className={styles.mode}>{MODE_LABEL[target.marginMode]}</span>
            </div>
            <dl className={styles.summaryGrid}>
              <div>
                <dt>Объём</dt>
                <dd className="num">{money(target.quantity, 6)}</dd>
              </div>
              <div>
                <dt>Маржа</dt>
                <dd className="num">{money(target.margin, 2, 2)}</dd>
              </div>
              <div>
                <dt>Вход · марк</dt>
                <dd className="num">
                  {price(target.entryPrice)} · {price(target.markPrice)}
                </dd>
              </div>
              <div>
                <dt>Ликвидация движком</dt>
                <dd className={cx('num', styles.liq)}>{price(target.liquidationPrice)}</dd>
              </div>
              <div>
                <dt>Результат сейчас</dt>
                <dd className="num">
                  <Signed value={target.unrealizedPnl} />
                </dd>
              </div>
            </dl>
          </div>
        ) : null}
        {inProfit && target?.marginMode === 'isolated' ? (
          <Callout tone="warning" title="Позиция сейчас в плюсе">
            Ручная ликвидация всё равно спишет изолированную маржу целиком — прибыль пользователь не получит. Убедитесь, что это нужная позиция.
          </Callout>
        ) : null}
        <p className={styles.modalNote}>
          Расчёт тот же, что у движка: изолированная маржа списывается целиком — убыток в счёт позиции, остаток в страховой фонд; у кросс-позиции списывается убыток.
          Пользователь получит уведомление о ликвидации. Действие необратимо и попадает в аудит.
        </p>
        <Field label="Причина" hint="Останется в аудите. Не короче трёх символов.">
          <Textarea value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Например: фид остановился, движок не может ликвидировать позицию" />
        </Field>
        <Field
          label="Цена (необязательно)"
          hint="Только если фид устарел или неверен: цена, по которой считается ликвидация, сохранится в аудите как цена оператора. Пусто — цена фида."
          error={priceValid ? null : 'Цена — положительное число с точкой, например 84450.5'}
        >
          <Input mono value={priceInput} onChange={(event) => setPriceInput(event.target.value.replace(',', '.').replace(/[^0-9.]/g, ''))} placeholder="Пусто — цена фида" inputMode="decimal" />
        </Field>
      </Modal>
    </>
  )
}
